import { X } from "lucide-react";
import type { Facets } from "../../data/vehicles";
import type { InventoryFilters } from "../../hooks/useInventoryFilters";
import { CheckOption, FilterGroup } from "./controls";
import PriceRange from "./PriceRange";

/* "Filter by" — the whole panel, top to bottom.

   Four questions, and deliberately only four: what it can cost, who made it,
   which of their cars, and what colour. A showroom this size sells on the
   badge and the budget; every other axis the API can narrow on was a box a
   visitor read past on the way to the ones they actually use.

   The order is the one a buyer thinks in — the money first, because it is the
   only filter that rules cars out rather than picking them, then the make,
   then the model under it, then the paint.

   Every option and every count on it comes from the API, counted over the
   same floor it filters — so the number beside a box and the number of cars
   ticking it returns are worked out once, in one place, rather than agreed
   between two. The models are counted over the chosen makes as well as
   listed under them: pick Honda and the group holds Honda's models and
   nothing else, because a model list that ignored the makes would offer a
   Civic under Toyota. */

type FilterPanelProps = {
  filters: InventoryFilters;
  facets: Facets;
  activeCount: number;
  setPrice: (range: { from: number; to: number }) => void;
  toggle: (key: "brands" | "models" | "colours", entry: string) => void;
  clearAll: () => void;
  /** Renders the dismiss control — the sheet on a phone has one, the column does not. */
  onClose?: () => void;
};

function FilterPanel({
  filters,
  facets,
  activeCount,
  setPrice,
  toggle,
  clearAll,
  onClose,
}: FilterPanelProps) {
  const chosen = (entries: string[]) =>
    entries.length ? entries.join(", ") : undefined;

  return (
    /* A flex column, and the groups below scroll inside it rather than the
       whole column scrolling: the height it is handed belongs to the card, so
       the track runs down the inside of the border with the header held above
       it, rather than down the gutter beside it. Left unconstrained — the
       sheet on a phone — the body is its natural height and nothing scrolls. */
    <div className="flex flex-col overflow-hidden rounded-2xl border border-line bg-raised shadow-card">
      {/* ---------- header ---------- */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-5 py-4">
        <h3 className="font-display text-[15px] font-semibold tracking-[-0.01em] text-platinum">
          Filter by
        </h3>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={clearAll}
            disabled={activeCount === 0}
            className="text-[12px] text-steel underline-offset-4 transition-colors hover:text-lime-deep hover:underline disabled:pointer-events-none disabled:opacity-45"
          >
            Reset all
          </button>

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close filters"
              className="grid size-7 place-items-center rounded-lg text-steel transition-colors hover:bg-surface hover:text-platinum"
            >
              <X aria-hidden="true" strokeWidth={1.8} className="size-4" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* ---------- what it costs ---------- */}
        <FilterGroup title="Price range">
          <PriceRange
            min={facets.minPrice}
            max={facets.maxPrice}
            prices={facets.prices}
            from={filters.minPrice ?? facets.minPrice}
            to={filters.maxPrice ?? facets.maxPrice}
            onChange={setPrice}
          />
        </FilterGroup>

        {/* ---------- who made it ---------- */}
        <FilterGroup
          title="Company"
          collapsible
          defaultOpen={filters.brands.length > 0}
          summary={chosen(filters.brands)}
        >
          <div className="grid grid-cols-2 gap-x-4">
            {facets.brands.map((brand) => (
              <CheckOption
                key={brand.name}
                label={brand.name}
                checked={filters.brands.includes(brand.name)}
                onChange={() => toggle("brands", brand.name)}
                count={brand.count}
              />
            ))}
          </div>
        </FilterGroup>

        {/* ---------- which of their cars ----------
          The list the API sends back is already the chosen makes' models, so
          this group is the cascade rather than a copy of it — and the caption
          says which makes it is standing under, because "Civic, City, BR-V"
          with no Toyota in sight otherwise reads as a floor that shrank. */}
        <FilterGroup
          title="Model"
          collapsible
          defaultOpen={filters.models.length > 0}
          summary={chosen(filters.models)}
        >
          {filters.brands.length > 0 ? (
            <p className="-mt-1 mb-3 text-[12px] text-steel">
              Models by {filters.brands.join(", ")}
            </p>
          ) : null}

          {facets.models.length > 0 ? (
            <div className="grid max-h-64 grid-cols-2 gap-x-4 overflow-y-auto overscroll-contain">
              {facets.models.map((model) => (
                <CheckOption
                  key={model.name}
                  label={model.name}
                  checked={filters.models.includes(model.name)}
                  onChange={() => toggle("models", model.name)}
                  count={model.count}
                />
              ))}
            </div>
          ) : (
            <p className="text-[12.5px] text-steel">
              Nothing on the floor under that company right now.
            </p>
          )}
        </FilterGroup>

        <FilterGroup
          title="Colour"
          collapsible
          defaultOpen={filters.colours.length > 0}
          summary={chosen(filters.colours)}
        >
          {/* One per line: a swatch, a paint's full name — "Graphite Grey",
            "Pearl White" — and its count need the width. */}
          <div className="grid grid-cols-1">
            {facets.colours.map((colour) => (
              <CheckOption
                key={colour.name}
                label={colour.name}
                swatch={colour.hex}
                checked={filters.colours.includes(colour.name)}
                onChange={() => toggle("colours", colour.name)}
                count={colour.count}
              />
            ))}
          </div>
        </FilterGroup>
      </div>
    </div>
  );
}

export default FilterPanel;
